import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plug } from 'lucide-react';
import { useToast } from '@/components/ui/toast';
import { ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';
import { fetchUstawieniaInstancji, zapiszUstawieniaInstancji } from '@/lib/ustawienia-instancji-api';

/**
 * OSOBY USUNIĘTE Z AUTOMATÓW W `/api/v1` (03.11.2026) — ustawienie instancji, domyślnie wyłączone.
 *
 * ⚠️ ZAMÓWIONE: „checkbox, czy udostępniamy pracowników usuniętych, ale trzymanych w postgresie jako
 * nieaktywni". Wyłączone = integracja ich nie widzi, a `usunieci` zgłasza je jako usunięte — reguła
 * stoi w API (`ustawienia-instancji.service.ts`), tu jest tylko przełącznik.
 *
 * ⚠️ BEZ OKNA POTWIERDZENIA: przestawienie da się cofnąć tym samym kliknięciem, a integracja i tak
 * dostaje „przejedź pełną listę", więc nic po drugiej stronie nie zostaje rozjechane.
 */
export function UsunieciWApi() {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuth();
  const mozeZapisywac = user?.role === 'ADMIN';

  const { data } = useQuery({ queryKey: ['ustawienia-instancji'], queryFn: fetchUstawieniaInstancji });

  const zapisz = useMutation({
    mutationFn: zapiszUstawieniaInstancji,
    onSuccess: (w) => qc.setQueryData(['ustawienia-instancji'], w),
    onError: (e) => toast.error(e instanceof ApiError ? e.message : t('common.error')),
  });

  return (
    <section className="space-y-3 rounded-lg border border-border bg-card p-4">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <Plug className="size-4" />
        {t('set.api.tytul')}
      </h2>
      <label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={data?.apiPokazujUsunietych ?? false}
          disabled={data === undefined || !mozeZapisywac || zapisz.isPending}
          onChange={(e) => zapisz.mutate({ apiPokazujUsunietych: e.target.checked })}
        />
        {t('set.api.usunieci')}
        {zapisz.isPending && <Loader2 className="size-3.5 animate-spin" />}
      </label>
      <p className="text-xs text-muted-foreground">{t('set.api.usunieciHint')}</p>
    </section>
  );
}
